import React, { SyntheticEvent, useState } from 'react'
import { InputTextarea } from 'primereact/inputtextarea'
import { RadioButton } from 'primereact/radiobutton'
import { hapticEffects } from '../../lib/hapticEffects'
import TextWithOptions from '../../modules/preview/components/TextWithOptions'
import Grid from '../_common/Grid/Grid'
import SkipBlock from '../..//modules/preview/components/SkipBlock'
import PrequalBlock from '../../modules/preview/components/PrequalBlock'

import useImages from '../../modules/images/useImages'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useTimelineFrame from '../../modules/timeline/useTimelineFrame'
import useViewport from '../../modules/viewport/useViewport'
import useSession from '../../session/useSession'
import usePreviewOptions from '../../modules/preview/usePreviewOptions'
import useOpenEndedLogger from '../../modules/supervisor/useOpenEndedLogger'
import useFirstMountEffect from '@aytm/lib/hooks/useFirstMountEffect'

import { IQuestionViewProps } from '../types'
import { ViewportOrientation } from '../../modules/viewport/types'
import { IRadioButtonsQuestionSchemaAnswer, RadioButtonsQuestionModelTypes } from './types'

import commonStyle from '../_common/style.module.scss'
import style from './style.module.scss'
import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'

const onCommentFocus = (event: SyntheticEvent) => {
  const el = event.target as HTMLInputElement
  if (!el) return
  el.setSelectionRange(el.value.length, el.value.length)
  hapticEffects.soft()
}

const RadioButtonsView = ({
  schema,
  response,
  timeline,
  responseValidation,
  showValidation,
  frame,
  onUpdateResponse
}: IQuestionViewProps<RadioButtonsQuestionModelTypes>) => {
  const session = useSession()
  const viewport = useViewport()
  const { localeOptions } = useInterfaceLocale()

  const hasSmallImages = schema.answers.some(answer => answer.image && !answer.image.expand)
  const { ImageAsset } = useImages()

  const { t } = useSurveyLocale()
  const frameInteractionStart = useTimelineFrame(timeline, frame)

  const disabled = false == session.rules?.show?.[schema.id] && !session.skipValidation
  const previewOptions = usePreviewOptions()

  const [iClicked, setIClicked] = useState(false)

  const sub = schema.parentId ? session.rules?.sub?.[schema.parentId] : session.rules?.sub?.[schema.id]

  const onSelectedChange = (answerIndex: number, answerId?: string) => {
    if (iClicked) {
      setIClicked(false)
      return
    }
    // if (event?.target && (event.target as Element).classList.contains('icon')) return
    timeline._log({ type: 'answer', target: answerId })

    const responseAnswer = response[answerIndex]
    if (!responseAnswer?.selected) {
      hapticEffects.selection()
    }

    onUpdateResponse(
      response.map(
        (responseAnswer, responseAnswerIndex) => (
          (responseAnswer.selected = responseAnswerIndex === answerIndex), responseAnswer
        )
      )
    )
  }

  const onKeySelect = (event: React.KeyboardEvent, idx: number, id: string) => {
    if ([' ', 'Enter'].includes(event.key)) {
      onSelectedChange(idx, id)
    }
  }

  const onImageClicked = (event: SyntheticEvent, answerIndex: number, answer: IRadioButtonsQuestionSchemaAnswer) => {
    if (
      viewport.mobile || viewport.touch ? !answer?.image?.expand : !(event.target as HTMLElement).closest('.answerRow')
    ) {
      setIClicked(true)
      setTimeout(() => {
        setIClicked(false)
      }, 100)
      return
    }
    const el = event.target as Element
    if (
      event?.target &&
      ('I' == el.tagName ||
        el.classList.contains('p-component-overlay') ||
        el.classList.contains('p-image-preview') ||
        el.parentElement?.classList.contains('p-image-preview-enter-done'))
    )
      return

    onSelectedChange(answerIndex, answer?.id)
  }

  const openEndedLogger = useOpenEndedLogger(timeline, schema.id)
  useFirstMountEffect(() => void response.forEach(answer => void openEndedLogger(answer.comment, answer.id, false)))

  const onCommentChange = (answerIndex: number, event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdateResponse(
      response.map((responseAnswer, responseAnswerIndex) => {
        if (responseAnswerIndex === answerIndex) {
          responseAnswer.comment = event.target.value
          openEndedLogger(responseAnswer.comment, responseAnswer.id)
        }
        return responseAnswer
      })
    )
  }

  return (
    <fieldset
      role="radiogroup"
      data-testid="SW-RB"
      aria-describedby={`questionNote-${schema.id}`}
      className={`
        ${style.RadioButtonsView} ${localeOptions.rtl ? style.RadioButtonsViewRTL : ''}
        ${viewport.mobile ? commonStyle.mobileView + ' ' + style.mobile : ''}
        ${disabled ? commonStyle.disabled : ''}
      `}
    >
      {viewport.mobile && viewport.orientation == ViewportOrientation.PORTRAIT && schema.image_grid_columns > 2 ? (
        ''
      ) : !schema.answers.filter(answer => !answer.hidden).length ? (
        t('questions', 'noAnswers')
      ) : (
        <>
          <div
            id={`questionNote-${schema.id}`}
            data-testid="SW-RB-ValidationMessage"
            className={`
              ${commonStyle.validationMessage}
              ${showValidation && !responseValidation.valid ? commonStyle.error : ''}
            `}
            dangerouslySetInnerHTML={{
              __html: `${t('questions', 'rb', 'note')}`
            }}
          />
          <legend className={commonStyle.srOnly}>{schema.text}</legend>
        </>
      )}
      {schema.image_grid_columns ? (
        <Grid
          schema={schema}
          response={response}
          onSelectedChange={onSelectedChange}
          onCommentChange={onCommentChange}
          showValidation={showValidation}
          role="radio"
        />
      ) : (
        schema.answers.map((answer, answerIndex) => {
          const responseAnswer = response[answerIndex]
          return (
            <div
              key={answer.id}
              className={`
                ${style.AnswerFrame}
                ${answer.hidden ? commonStyle.hidden : ''}
                ${answer.fakeHidden ? commonStyle.fakeHidden : ''}
              `}
              data-testid="SW-RB-Answer"
            >
              <div className={`${style.AnswerRow} answerRow`}>
                <div className={style.noWrap}>
                  {answer.image && !answer.image?.expand && (
                    <div onClick={event => onImageClicked(event, answerIndex, answer)} data-testid="SW-RB-Answer-Image">
                      <ImageAsset
                        asset={answer.image}
                        className={style.AnswerImage}
                        width={66}
                        height={36}
                        size="small"
                        position={localeOptions.rtl ? 'right' : 'left'}
                        previewLabelTemplate={() => (
                          <TextWithOptions text={answer.text} options={previewOptions} rules={sub?.[answer.id]} />
                        )}
                      />
                    </div>
                  )}

                  {(!answer.image || answer.image.expand) && hasSmallImages && (
                    <ImageAsset width={66} height={36} size="small" asset={null} className={style.AnswerImage} />
                    // <div className={style.AnswerVoidImage} />
                  )}
                  <label
                    htmlFor={answer.id}
                    className={style.AnswerLabel}
                    onClick={() => onSelectedChange(answerIndex, answer.id)}
                    onKeyDown={event => onKeySelect(event, answerIndex, answer.id)}
                  >
                    <RadioButton
                      value={answer.id}
                      name="answer"
                      data-testid="SW-RB-Answer-RadioButton"
                      checked={responseAnswer?.selected}
                      inputId={answer.id}
                    />
                    <div className={commonStyle.srOnly} aria-live="polite">
                      {responseAnswer?.selected && answer.oe !== 'off' ? `${t('questions', 'oe', 'hint')}` : ''}
                    </div>
                    <div className={style.AnswerTextBlock}>
                      <span className={style.AnswerText} data-testid="SW-RB-Answer-Text">
                        {(previewOptions.showHidden || !session.rules?.hide_text?.[answer.id]?.condition) && (
                          <TextWithOptions text={answer.text} options={previewOptions} rules={sub?.[answer.id]} />
                        )}
                      </span>
                    </div>
                    <div className={style.rightBlock}>
                      {previewOptions.showHints && <SkipBlock rules={session.rules?.skip?.[answer.id]} />}
                      {previewOptions.showHints && schema.is_prequal && <PrequalBlock accept={answer.accept} />}
                    </div>
                  </label>
                </div>
                {answer.oe && 'off' !== answer.oe && responseAnswer?.selected && (
                  <label
                    htmlFor={`textarea-${answer.id}`}
                    className={style.AnswerTextAreaLabel}
                    aria-label={answer.text}
                  >
                    <InputTextarea
                      id={`textarea-${answer.id}`}
                      className={`${style.AnswerTextArea} ${
                        showValidation && !responseAnswer?.comment?.length && 'mandatory' == answer.oe
                          ? style.AnswerTextAreaError
                          : ''
                      }  ${hasSmallImages ? style.AnswerTextAreaWithImage : ''}`}
                      placeholder={t('questions', 'placeholder')}
                      aria-required={'mandatory' === answer.oe}
                      required={'mandatory' === answer.oe}
                      rows={3}
                      autoResize={true}
                      autoFocus={false}
                      value={responseAnswer?.comment}
                      data-testid="SW-RB-Answer-OE"
                      onChange={event => onCommentChange(answerIndex, event)}
                      onFocus={onCommentFocus}
                    />
                  </label>
                )}
              </div>
              {answer.image && answer.image?.expand && (
                <div
                  onClick={event => onImageClicked(event, answerIndex, answer)}
                  className={style.expandWrapper}
                  data-testid="SW-Question-ExpandedImage"
                >
                  <ImageAsset
                    asset={answer.image}
                    className={style.AnswerBigImage}
                    width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                    size="medium"
                    onFullImageLoaded={frameInteractionStart}
                    previewLabelTemplate={() => (
                      <TextWithOptions text={answer.text} options={previewOptions} rules={sub?.[answer.id]} />
                    )}
                  />
                </div>
              )}
            </div>
          )
        })
      )}
    </fieldset>
  )
}

export default RadioButtonsView
